import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import Layout from '../components/layout/Layout';
import RouteGuard from './RouteGuard';
import { routes } from './routes';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            gap: 2,
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Đã xảy ra lỗi
            </Typography>
            <Typography variant="body2">
              Không thể tải trang này. Vui lòng thử lại sau.
            </Typography>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Suspense Loading Component
const SuspenseLoader = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      gap: 2,
    }}
  >
    <CircularProgress size={32} />
    <Typography variant="body2" color="text.secondary">
      Đang tải trang...
    </Typography>
  </Box>
);

// Route wrapper component
const RouteWrapper = ({ route }) => {
  const { 
    element: Component, 
    requiresAuth, 
    requiresGuest, 
    roles, 
    isPublic 
  } = route;

  // Handle redirect routes
  if (route.redirect) {
    return <Navigate to={route.redirect} replace />;
  }

  // Create the component element
  const element = typeof Component === 'function' && Component.prototype?.isReactComponent 
    ? <Component />  // Class component
    : typeof Component === 'function' 
      ? <Component />  // Function component
      : <Component />; // React.lazy component

  // For public routes that don't need layout
  if (isPublic) {
    return (
      <RouteGuard requiresGuest={requiresGuest}>
        {element}
      </RouteGuard>
    );
  }

  // For protected routes with layout
  return (
    <RouteGuard 
      requiresAuth={requiresAuth} 
      roles={roles}
    >
      <Layout>
        {element}
      </Layout>
    </RouteGuard>
  );
};

// 404 Not Found Component
const NotFound = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      gap: 2,
      textAlign: 'center',
      p: 3,
    }}
  >
    <Typography variant="h4" color="error">
      404
    </Typography>
    <Typography variant="h6" color="text.secondary">
      Trang không tồn tại
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Trang bạn đang tìm kiếm không tồn tại hoặc đã bị di chuyển.
    </Typography>
  </Box>
);

// Main AppRouter Component
const AppRouter = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader />}>
          <Routes>
            {routes.map((route, index) => (
              <Route
                key={route.path || index}
                path={route.path}
                element={<RouteWrapper route={route} />}
              />
            ))}
            
            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default AppRouter; 